import { LoaderService } from '@/app/common/services/loader/loader.service'
import { CommonModule, Location } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { Component, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Params, RouterModule } from '@angular/router'
import { BehaviorSubject } from 'rxjs'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

import { NzButtonModule } from 'ng-zorro-antd/button'
import { NzIconModule } from 'ng-zorro-antd/icon'
import { NzModalService } from 'ng-zorro-antd/modal'
import {
  NzNotificationModule,
  NzNotificationService,
} from 'ng-zorro-antd/notification'
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm'
import { NzTagModule } from 'ng-zorro-antd/tag'

import { City } from '@/app/modules/city/core/type'
import { CityService } from '@/app/modules/city/core/service'
import { Checklist } from '@/app/modules/checklist/core/type'
import { ChecklistService } from '@/app/modules/checklist/core/service'
import {
  ChecklistHeader,
  HeaderTypeEnum,
  StatusEnum,
} from '@/app/modules/checklist-header/core/type'
import { ChecklistHeaderService } from '@/app/modules/checklist-header/core/service'
import { ImagesService } from '@/app/modules/images/core/service'
import { EditHeaderComponent } from '../../components/edit-header/edit-header.component'
import { ItemComponent } from '../../components/item/item.component'
import { NotProceedComponent } from '../../components/not-proceed/not-proceed.component'

@Component({
  templateUrl: './items.component.html',
  styleUrls: ['./items.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NzButtonModule,
    NzIconModule,
    NzNotificationModule,
    NzPopconfirmModule,
    NzTagModule,
    ItemComponent,
    EditHeaderComponent,
    NotProceedComponent,
  ],
  providers: [NzModalService],
})
export class ListChecklistComponent implements OnInit {
  checklistHeader?: ChecklistHeader

  checklistFound: Checklist[] = []

  checklistTotal = 0

  checklistLoader = new BehaviorSubject<boolean>(false)

  imagesLoader = new BehaviorSubject<boolean>(false)

  checklistHeaderId!: number

  StatusEnum = StatusEnum

  originCity!: City

  destinationCity!: City

  constructor(
    private checklistService: ChecklistService,
    private checklistHeaderService: ChecklistHeaderService,
    private notificationService: NzNotificationService,
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private imageService: ImagesService,
    private cityService: CityService,
    private location: Location,
  ) {}

  ngOnInit(): void {
    this.handleOpenChecklistHeader()
  }

  get isMaintenance(): boolean {
    return this.checklistHeader?.type === 'maintenance'
  }

  get statusInfo(): { text: string; color: string } {
    switch (this.checklistHeader?.status) {
      case StatusEnum.IN_PROGRESS:
        return { text: 'Em andamento', color: 'orange' }
      case StatusEnum.DONE:
        return { text: 'Finalizado', color: 'green' }
      case StatusEnum.CANCELED:
        return { text: 'Cancelado', color: 'red' }
      case StatusEnum.NOT_PROCEED:
        return { text: 'Não procede', color: 'gold' }
      default:
        return { text: 'Desconhecido', color: 'default' }
    }
  }

  handleOpenChecklistHeader() {
    this.route.params.subscribe((params: Params) => {
      if (!params['id']) return
      this.checklistHeaderId = params['id']
      this.handleGetChecklistHeaderById(this.checklistHeaderId)
      this.handleSearchChecklist(this.checklistHeaderId)
    })
  }

  handleSearchChecklist(
    id: number,
    pageIndex: number = 1,
    pageSize: number = 10000
  ) {
    this.loaderService
      .executeWithLoader(
        this.checklistService.findAll({
          checklist_header_id: this.checklistHeaderId,
          pageIndex,
          pageSize
        }),
        this.checklistLoader
      )
      .subscribe({
        next: ({ items, total }) => {
          this.checklistFound = items
          this.checklistTotal = total

        },
        error: (error: HttpErrorResponse) => {
          this.notificationService.error(`${error.status}`, error.error.message)
        }
      })
  }

  handleGetChecklistHeaderById(id: number) {
    this.loaderService
      .executeWithLoader(
        this.checklistHeaderService.getById(id),
        this.checklistLoader
      )
      .subscribe({
        next: checklistHeader => {
          this.checklistHeader = checklistHeader

          if (
            this.checklistHeader.origin_id &&
            this.checklistHeader.destination_id
          ) {
            this.getOriginCity(this.checklistHeader.origin_id)
            this.getDestination(this.checklistHeader.destination_id)
          }
        },
        error: (error: HttpErrorResponse) => {
          this.notificationService.error(`${error.status}`, error.error.message)
        }
      })
  }

  cancel() {}

  confirm(status: StatusEnum) {
    if (!this.checklistHeaderId) {
      this.notificationService.error('Erro', 'ID do checklist não encontrado.')
      return
    }
    this.checklistHeaderService
      .update(this.checklistHeaderId, { status: status })
      .subscribe({
        next: () => {
          this.handleGetChecklistHeaderById(this.checklistHeaderId)
          const entity = this.isMaintenance ? 'Solicitação' : 'Checklist'
          if (status === StatusEnum.CANCELED) {
            this.notificationService.success(
              'Sucesso',
              `${entity} cancelada com sucesso`,
            )
            return
          }
          this.notificationService.success(
            'Sucesso',
            `${entity} finalizada com sucesso`,
          )
        },
        error: (error: HttpErrorResponse) => {
          this.notificationService.error(
            `${error.status}`,
            error.error?.message || 'Erro desconhecido',
          )
        },
      })
  }

  getOriginCity(cityId: number) {
    this.cityService.getById(cityId).subscribe({
      next: (response: City) => {
        this.originCity = response
        if (this.checklistHeader) this.checklistHeader.origin = response
      },
      error: err => {
        console.log(err.error.message)
      }
    })
  }

  getDestination(cityId: number) {
    this.cityService.getById(cityId).subscribe({
      next: (response: City) => {
        this.destinationCity = response
        if (this.checklistHeader)
          this.checklistHeader.destination = response
      },
      error: err => {
        console.log(err.error.message)
      }
    })
  }

  async generatePdf() {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const xPosition = 40
    const yPosition = 30

    let productImage = '.PNG'

    if (!ctx) return

    canvas.width = 500
    canvas.height = 700

    if (this.checklistHeader?.ProductModel.image) {
      const imageUrl = this.checklistHeader?.ProductModel.image
      const image = await this.imageService.photo(imageUrl).toPromise()

      productImage = imageUrl
      if (image) await this.drawImageOnCanvas(ctx, image)
    }

    const currentYPosition = 150
    const pdf = new jsPDF('p', 'pt', 'a4')

    const formatDate = (date: any) => {
      return new Date(date).toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const checklistType = (type?: HeaderTypeEnum): string => {
      switch (type) {
        case 'used':
          return 'Usado'
        case 'transfer_start':
          return 'Transferência Enviada'
        case 'transfer_done':
          return 'Transferência Recebida'
        case 'new':
          return 'Novo'
        case 'maintenance':
          return 'Manutenção'
        default:
          return 'Transferência Enviada'
      }
    }

    pdf.setFontSize(14)
    pdf.text(
      'Check-list de condições Grupo Suprema',
      xPosition + 5,
      yPosition - 10
    )

    const tableData = [
      ['Modelo', this.checklistHeader?.ProductModel.name || 'N/A'],
      ['Tipo', checklistType(this.checklistHeader?.type) || 'N/A'],
      ['Responsável', this.checklistHeader?.UserModel.name || 'N/A'],
      ['Chassis', this.checklistHeader?.chassis.toUpperCase() || 'N/A'],
      ['Data', formatDate(this.checklistHeader?.date) || 'N/A'],
      ['Horímetro', this.checklistHeader?.hours_meter?.toString() || 'N/A']
    ]

    if (
      this.checklistHeader?.origin_id &&
      this.checklistHeader?.destination_id &&
      this.checklistHeader.origin
    ) {
      tableData.push(['Origem', this.originCity.name || 'N/A'])
      tableData.push(['Destino', this.destinationCity.name || 'N/A'])
    }
    autoTable(pdf, {
      startY: yPosition - 5,
      body: tableData,
      headStyles: {
        fillColor: '#c0142c',
        textColor: '#ffffff'
      },
      theme: 'striped'
    })

    pdf.setFontSize(12)
    const img = new Image()
    img.src = canvas.toDataURL()
    img.width = 360

    pdf.addImage(
      canvas.toDataURL(),
      productImage.split('.')[1],
      xPosition + 300,
      10,
      img.width,
      img.height
    )
    const data = this.checklistFound.map((item, index) => {
      const images = item.ChecklistImageModels ?? []
      return {
        index: index + 1,
        question: item.ChecklistQuestionModel.question,
        answer: item.answer,
        image: images.length > 0 ? String(images.length) : 'Não',
        imageSrcs: images.map(img => img.image_url).filter(src => !!src)
      }
    })

    autoTable(pdf, {
      startY: currentYPosition + 60,
      html: ``,
      head: [['N', 'Pergunta', 'Resposta', 'Imagem']],
      headStyles: {
        fillColor: '#c0142c',
        textColor: '#ffffff'
      },
      body: data.map(item => [
        item.index,
        item.question,
        item.answer,
        item.image
      ]),
      styles: { fontSize: 10, cellPadding: 10, cellWidth: 'wrap' },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 300 },
        2: { cellWidth: 80 },
        3: { cellWidth: 80 }
      }
    })

    let previousYPosition = ((pdf as any).lastAutoTable.finalY || 20) + 40
    const pageHeight = pdf.internal.pageSize.height

    for (const item of data) {
      for (let photoIndex = 0; photoIndex < item.imageSrcs.length; photoIndex++) {
        const imageSrc = item.imageSrcs[photoIndex]
        try {
          const image = await this.imageService.photo(imageSrc).toPromise()

          if (image) {
            await this.loadImage(image)

            const pageWidth = pdf.internal.pageSize.width
            const fixedWidth = 400
            const fixedHeight = 350

            const centerX = (pageWidth - fixedWidth) / 2

            if (previousYPosition + fixedHeight + 50 > pageHeight) {
              pdf.addPage()
              previousYPosition = 20
            }

            pdf.setFontSize(12)
            const question = item.question
            const questionLines =
              question.length > 50
                ? pdf.splitTextToSize(question, pageWidth - 40)
                : [question]
            const caption = `${photoIndex + 1}/${item.imageSrcs.length}`

            pdf.text(questionLines, pageWidth / 2, previousYPosition, {
              align: 'center'
            })
            pdf.text(caption, pageWidth / 2, previousYPosition + questionLines.length * 14, {
              align: 'center'
            })

            let imageY = previousYPosition + questionLines.length * 14 + 10
            const imageMeta =
              this.checklistFound[item.index - 1]?.ChecklistImageModels?.[photoIndex]
            if (imageMeta?.replaced_at) {
              const name = imageMeta.ReplacedByUser?.name || 'Administrador'
              const date = new Date(imageMeta.replaced_at).toLocaleString('pt-BR')
              pdf.setFontSize(10)
              pdf.text(
                `Foto substituída por ${name} em ${date}`,
                pageWidth / 2,
                imageY,
                { align: 'center' }
              )
              imageY += 16
            }

            pdf.addImage(
              image,
              this.imageFormat(imageSrc),
              centerX,
              imageY,
              fixedWidth,
              fixedHeight
            )

            previousYPosition = imageY + fixedHeight + 30
          }
        } catch (error) {
          console.error('Erro ao carregar a imagem:', imageSrc, error)
        }
      }
    }

    pdf.save(`checklist-${this.checklistHeader?.chassis}.pdf`)
  }

  private imageFormat(src: string): string {
    const ext = src.split('?')[0].split('.').pop()?.toLowerCase()
    if (ext === 'png') return 'PNG'
    if (ext === 'webp') return 'WEBP'
    if (ext === 'gif') return 'GIF'
    return 'JPEG'
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.src = src
      img.width = 450
      img.onload = () => resolve(img)
      img.onerror = err => reject(err)
    })
  }

  async fetchImageAsDataURL(imageUrl: string): Promise<string> {
    const response = await fetch(imageUrl)
    const blob = await response.blob()

    return new Promise(resolve => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.readAsDataURL(blob)
    })
  }

  async drawImageOnCanvas(ctx: CanvasRenderingContext2D, imageUrl: string) {
    const dataUrl = await this.fetchImageAsDataURL(imageUrl)

    return new Promise(resolve => {
      const img = new Image()
      img.src = dataUrl
      img.onload = () => {
        ctx.drawImage(img, 50, 50, 200, 150)
        resolve(true)
      }
    })
  }

  handleEditChecklistHeader(data: any) {
    this.checklistHeaderService.getById(data.id).subscribe({
      next: () => {
        this.handleOpenChecklistHeader()
      },
      error: (error: HttpErrorResponse) => {
        this.notificationService.error(`${error.status}`, error.error.message)
      }
    })
  }

  handleNotProceed(data: any) {
    this.checklistHeaderService.getById(data.id).subscribe({
      next: () => {
        this.handleOpenChecklistHeader()
      },
      error: (error: HttpErrorResponse) => {
        this.notificationService.error(`${error.status}`, error.error.message)
      }
    })
  }

  backTo(): void {
    this.location.back()
  }
}
